import { createContext } from 'react';
import { decorate, observable, action, computed, toJS } from 'mobx';

import * as StorageKeys from '../constants/storageKeys';
import * as fn from '../utilities/_functions';
import * as eh from '../utilities/examHelper';

import api from '../api';

export class PredefinedExamSelect {
    examTemplateId = null;
    userPredefinedTemplates = [];
    sharedPredefinedTemplates = [];
    excludeIds = [];
    appointment = null;
    selectedId = null;
    isLoading = false;
    isSaving = false;
    isReady = false;

    cancelPublicPredefinedData = null;
    cancelUserPredefinedData = null;

    initialize = (appointment, examTemplateId, excludeIds) => {
        const that = this;
        this.clear();

        if (appointment) {
            this.appointment = appointment;
            this.examTemplateId = eh.getDefaultExamTemplateId(appointment.services);
        }

        if (examTemplateId) {
            this.examTemplateId = examTemplateId;
        }

        this.excludeIds = excludeIds && excludeIds.length > 0 ? excludeIds : [];

        return that.refresh()
            .finally(() => {
                that.isReady = true;
            })
    }

    refresh = () => {
        const that = this;

        return new Promise((resolve, reject) => {
            const currentUser = JSON.parse(window.localStorage.getItem(StorageKeys.CURRENT_USER));
            that.isLoading = true;

            const searchParameters = [
                { field: 'DeactivatedDateUtc', value: null },
                { field: 'ExamTemplateId', value: that.examTemplateId },
                { field: 'Id', operator: 'NotContains', value: that.excludeIds.map(e => { return e }).join(',') },
            ];
            const searchSortFields = [{ field: 'Name', direction: 'ASC' }];

            Promise.all([
                api.PredefinedData.search(
                    {
                        parameters: [
                            ...searchParameters,
                            ...[{ field: 'UserId', value: currentUser.id, }]
                        ],
                        sortByFields: searchSortFields,
                    },
                    (c) => { that.cancelUserPredefinedData = c }
                ),
                api.PredefinedData.search(
                    {
                        parameters: [
                            ...searchParameters,
                            ...[{ field: 'UserId', value: null, }]
                        ],
                        sortByFields: searchSortFields,
                    },
                    (c) => { that.cancelPublicPredefinedData = c }
                ),
            ])
                .then(response => {
                    const userPredefinedDataSets = response[0].data.result ? response[0].data.result : [];
                    const publicPredefinedDataSets = response[1].data.result ? response[1].data.result : [];

                    that.userPredefinedTemplates = userPredefinedDataSets;
                    that.sharedPredefinedTemplates = publicPredefinedDataSets;

                    if (!that.selectedId) {
                        if (that.userPredefinedTemplates && that.userPredefinedTemplates.length > 0) {
                            that.selectedId = that.userPredefinedTemplates[0].id;
                        }
                        else if (that.sharedPredefinedTemplates && that.sharedPredefinedTemplates.length > 0) {
                            that.selectedId = that.sharedPredefinedTemplates[0].id;
                        }
                    }

                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isLoading = false;
                })
        })
    }

    save = () => {
        const that = this;

        return new Promise((resolve) => {
            resolve(toJS(that.selectedData));
        })
    }

    clear = () => {
        this.examTemplateId = null;
        this.selectedId = null;
        this.userPredefinedTemplates.clear();
        this.sharedPredefinedTemplates.clear();
        this.excludeIds.clear();
        this.appointment = null;
        this.isLoading = false;
        this.isSaving = false;
        this.isReady = false;

        if (fn.isFunction(this.cancelPublicPredefinedData)) {
            this.cancelPublicPredefinedData();
            this.cancelPublicPredefinedData = null;
        }

        if (fn.isFunction(this.cancelUserPredefinedData)) {
            this.cancelUserPredefinedData();
            this.cancelUserPredefinedData = null;
        }
    }

    get selectedData() {
        if (this.filteredDataSets && this.filteredDataSets.length > 0) {
            return this.filteredDataSets.filter(d => d.id === this.selectedId)[0];
        }

        return null;
    }

    get filteredDataSets() {
        const allDataSets = [
            ...(this.userPredefinedTemplates && this.userPredefinedTemplates.length > 0 ? this.userPredefinedTemplates && this.userPredefinedTemplates : []),
            ...(this.sharedPredefinedTemplates && this.sharedPredefinedTemplates.length > 0 ? this.sharedPredefinedTemplates && this.sharedPredefinedTemplates : [])
        ];

        if (allDataSets && allDataSets.length > 0 && this.examTemplateId) {
            return allDataSets.some(d => d.examTemplateId === this.examTemplateId) ? allDataSets.filter(d => d.examTemplateId === this.examTemplateId) : [];
        }

        return allDataSets && allDataSets.length > 0 ? allDataSets : [];
    }
}

decorate(PredefinedExamSelect, {
    examTemplateId: observable,
    selectedId: observable,
    userPredefinedTemplates: observable,
    sharedPredefinedTemplates: observable,
    excludeIds: observable,
    appointment: observable,
    isLoading: observable,
    isSaving: observable,
    isReady: observable,
    save: action,
    selectedData: computed,
    filteredDataSets: computed,
})

export default createContext(new PredefinedExamSelect());